import { useEffect, useId, useReducer, useRef, useState } from 'react'
import { Button, Callout, Field, FieldRow, Paragraph, Select, StatusLine, Text } from 'admination-react'
import { teilnehmendeLoeschen, testdatenVerfuegbar } from '@/core/api/testdaten'
import { currentVariant, me } from '@/core/data/session'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent, type WriteIdentity } from '@/core/api/http'
import { trackSettingsWrite } from '@/core/api/settingsWrites'
import { descriptionFor } from '@/variants'
import { ANFANG, laeuft, weiter, zaehlungText, type Ereignis } from './testdatenZustand'

export function anzahlAusEingabe(eingabe: string): number | null {
  if (!/^\d+$/.test(eingabe.trim())) return null
  const n = Number(eingabe)
  return n >= 1 && n <= 10 ? n : null
}

export function TestdatenSection() {
  const [identity] = useState(captureWriteIdentity)
  const running = useRef(false)
  const [verfuegbar, setVerfuegbar] = useState(false)
  const [variantName, setVariantName] = useState('')
  const [zustand, senden] = useReducer(weiter, ANFANG)
  const anlage = descriptionFor(currentVariant()).testdaten
  const [schritt, setSchritt] = useState(anlage?.schritte[0]?.key ?? '')
  const [anzahl, setAnzahl] = useState('1')
  const anzahlId = useId()
  const schrittId = useId()
  const varianteId = useId()

  useEffect(() => {
    let cancelled = false
    void testdatenVerfuegbar().then(async (ja) => {
      if (cancelled || !ja || !isWriteIdentityCurrent(identity)) return
      const { variant, variants, isOwner } = await me()
      if (cancelled || !isOwner || !isWriteIdentityCurrent(identity)) return
      setVariantName(variants.find((v) => v.key === variant)?.name ?? variant)
      setVerfuegbar(true)
    }).catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [identity])

  if (!verfuegbar) return null

  const beschaeftigt = laeuft(zustand)
  const gesperrt = zustand.phase !== 'bereit'
  const zahl = anzahlAusEingabe(anzahl)

  async function ausfuehren(label: string, work: (owner: WriteIdentity) => Promise<Ereignis>) {
    if (running.current || !isWriteIdentityCurrent(identity)) return
    running.current = true
    try {
      const ergebnis = await trackSettingsWrite(work)
      if (isWriteIdentityCurrent(identity)) senden(ergebnis)
    } catch (error) {
      if (isWriteIdentityCurrent(identity)) {
        senden({ typ: 'fehler', text: `${label} fehlgeschlagen: ${friendlyErrorMessage(error, 'Die Aktion konnte nicht abgeschlossen werden.')}` })
      }
    } finally {
      running.current = false
    }
  }

  function zaehlen() {
    if (zustand.phase !== 'bereit') return
    return ausfuehren('Zählen', async (owner) => {
      senden({ typ: 'zaehlen' })
      return { typ: 'gezaehlt', zaehlung: await teilnehmendeLoeschen(false, owner) }
    })
  }

  function loeschen() {
    if (zustand.phase !== 'bestaetigen') return
    return ausfuehren('Löschen', async (owner) => {
      senden({ typ: 'loeschen' })
      return { typ: 'geloescht', ergebnis: await teilnehmendeLoeschen(true, owner) }
    })
  }

  function anlegen(n: number) {
    if (!anlage || zustand.phase !== 'bereit') return
    return ausfuehren('Anlegen', async (owner) => {
      senden({ typ: 'anlegen' })
      await anlage.anlegen(schritt, n, (text) => {
        if (isWriteIdentityCurrent(owner)) senden({ typ: 'fortschritt', text })
      }, owner)
      return { typ: 'angelegt', anzahl: n }
    })
  }

  const zaehlung =
    zustand.phase === 'bestaetigen' || zustand.phase === 'loescht' ? zaehlungText(zustand.zaehlung) : null

  return (
    <div className="section" data-testid="testdaten">
      <h3 className="section-header">Testdaten (nur Staging)</h3>
      <div className="section-body">
        <Paragraph variant="muted">
          Löscht alle Teilnehmenden dieser Organisation oder legt Testteilnehmende auf einem gewählten Schritt an. Jede
          Person durchläuft dafür die Schritte davor wie im echten Ablauf. Ab „Förderplan erstellen“ dauert das länger,
          weil die KI den GEVA-Test liest und den Förderplan vorbelegt.
        </Paragraph>

        <FieldRow>
          <Button
            variant="danger"
            data-testid="testdaten-loeschen"
            disabled={gesperrt}
            onClick={() => void zaehlen()}
          >
            Alle Teilnehmenden dieser Organisation löschen
          </Button>
        </FieldRow>

        {zaehlung && (
          <>
            <Callout tone="warning" title={zaehlung.titel} data-testid="testdaten-zaehlung">
              {zaehlung.tabellen}
            </Callout>
            <FieldRow>
              <Button
                variant="danger"
                data-testid="testdaten-loeschen-bestaetigen"
                disabled={beschaeftigt}
                onClick={() => void loeschen()}
              >
                Endgültig löschen
              </Button>
              <Button disabled={beschaeftigt} onClick={() => senden({ typ: 'abbrechen' })}>
                Abbrechen
              </Button>
            </FieldRow>
          </>
        )}

        {anlage ? (
          <>
            <Field label="Maßnahmen-Art" htmlFor={varianteId}>
              <Select
                id={varianteId}
                data-testid="testdaten-variante"
                options={[{ value: currentVariant(), label: variantName }]}
                value={currentVariant()}
                disabled={gesperrt}
              />
            </Field>
            <Field label="Schritt" htmlFor={schrittId}>
              <Select
                id={schrittId}
                data-testid="testdaten-schritt"
                options={anlage.schritte.map((s) => ({ value: s.key, label: s.label }))}
                value={schritt}
                onChange={setSchritt}
                disabled={gesperrt}
              />
            </Field>
            <Field label="Anzahl" htmlFor={anzahlId} error={zahl === null ? 'Bitte eine Zahl von 1 bis 10.' : undefined}>
              <Text
                id={anzahlId}
                data-testid="testdaten-anzahl"
                type="number"
                min={1}
                max={10}
                value={anzahl}
                aria-invalid={zahl === null || undefined}
                onChange={(e) => setAnzahl(e.target.value)}
                disabled={gesperrt}
              />
            </Field>
            <FieldRow>
              <Button
                variant="primary"
                data-testid="testdaten-anlegen"
                disabled={gesperrt || zahl === null || schritt === ''}
                onClick={() => zahl !== null && void anlegen(zahl)}
              >
                Anlegen
              </Button>
            </FieldRow>
          </>
        ) : (
          <Paragraph variant="muted">Für diese Maßnahmen-Art gibt es noch keine Testteilnehmenden zum Anlegen.</Paragraph>
        )}

        {zustand.phase === 'legtAn' && (
          <StatusLine role="status" data-testid="testdaten-fortschritt">
            {zustand.fortschritt}
          </StatusLine>
        )}
        {zustand.phase === 'bereit' && zustand.ergebnis && (
          <StatusLine
            strong
            role={zustand.ergebnis.ton === 'error' ? 'alert' : 'status'}
            tone={zustand.ergebnis.ton}
            data-testid="testdaten-ergebnis"
          >
            {zustand.ergebnis.text}
          </StatusLine>
        )}
      </div>
    </div>
  )
}
